import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Bar } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';

import { ACCENT_COLOR, GRID_COLOR } from '../../constants/subscriberCohorts';
import {
  ChurnReason,
  formatInteger,
  formatPercent,
} from '../../models/SubscriberCohortsModel';

// Past this many bars the tail folds into "other reasons".
const MAX_REASONS = 10;
const BAR_ROW_HEIGHT_PX = 32;
const AXIS_BAND_HEIGHT_PX = 40;
const MAX_BAR_THICKNESS_PX = 24;
const BAR_RADIUS_PX = 4;

type Props = {
  reasons: ChurnReason[];
};

type ReasonBar = { label: string; total: number };

// Why the plans of the range that are paused or cancelled today went down.
// One series, so one colour and no legend: the title says what is plotted.
const CohortChurnReasonsChart: FC<Props> = ({ reasons }) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `subscriberCohorts.${id}` }, values);

  // Reasons arrive sorted by volume.
  const shown: ReasonBar[] = reasons.slice(0, MAX_REASONS).map((reason) => ({
    label: reason.label || t('noReason'),
    total: reason.total,
  }));
  const restTotal = reasons
    .slice(MAX_REASONS)
    .reduce((sum, reason) => sum + reason.total, 0);
  const bars = restTotal
    ? [...shown, { label: t('otherReasons'), total: restTotal }]
    : shown;
  const churned = reasons.reduce((sum, reason) => sum + reason.total, 0);

  const data: ChartData<'bar'> = {
    labels: bars.map((bar) => bar.label),
    datasets: [
      {
        label: t('churnChart'),
        data: bars.map((bar) => bar.total),
        backgroundColor: ACCENT_COLOR,
        maxBarThickness: MAX_BAR_THICKNESS_PX,
        borderRadius: BAR_RADIUS_PX,
        borderSkipped: 'start',
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: {
        grid: { color: GRID_COLOR },
        ticks: { callback: (value) => formatInteger(Number(value)) },
      },
      y: { grid: { display: false } },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item) =>
            t('churnShare', {
              count: formatInteger(item.parsed.x),
              share: formatPercent(churned ? item.parsed.x / churned : null),
            }),
        },
      },
    },
  };

  return (
    <div>
      <div className="fs-5 fw-bold mb-1">{t('churnChart')}</div>
      <div className="text-muted fs-8 mb-3">{t('churnChartHelp')}</div>
      {bars.length === 0 ? (
        <p className="text-muted">{t('noChurn')}</p>
      ) : (
        <div
          style={{
            height: `${bars.length * BAR_ROW_HEIGHT_PX + AXIS_BAND_HEIGHT_PX}px`,
          }}
        >
          <Bar data={data} options={options} />
        </div>
      )}
    </div>
  );
};

export default CohortChurnReasonsChart;
